import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type SetStateAction,
} from "react";
import { apiService, PusherService } from "../services";
import { config } from "../config/env.config";
import { generateUUID, isLocationPausedError } from "../utils";
import {
  Message,
  MessageRole,
  InterviewPhase,
  InterviewUIStatus,
  InterviewMessageResponseFormat,
  InterviewQuestionInputType,
  InputOption,
  RequestStates,
  ResponseFormat,
  SendMessageRequest,
  PreviousQuestionResponse,
  VoiceUrls,
} from "../types";

interface UseInterviewOptions {
  interviewLocationId: string;
  candidateId?: string;
  onError?: (error: string) => void;
  onLanguageChange?: (languageCode: string) => void;
  /** Whether to auto-start the interview on mount (default: true) */
  autoStart?: boolean;
  /** If set, resume this session instead of creating a new one */
  resumeSessionId?: string;
  /** Pre-loaded messages for resume mode */
  resumeMessages?: Message[];
  /** Pusher channel override (from resume continue/restart response) */
  resumePusherChannel?: string;
  /** Phase to restore when resuming (from API checkpoint) */
  resumePhase?: InterviewPhase;
  /** Server-enforced capability returned by resume/start APIs */
  resumeCanEditPreviousResponse?: boolean;
}

export interface SendMessageOptions {
  selectedOptionIds?: string[];
  displayContent?: string;
  inputType?: InterviewQuestionInputType;
  /** Voice answer S3 key — tagged alongside `content` so the API persists
   * `interview_session_message.audio_s3_key`. See SendMessageRequest. */
  audioS3Key?: string;
  /** Voice answers should explicitly pass `'voice'`; choice/written paths
   * still derive from the input shape below. */
  responseFormat?: InterviewMessageResponseFormat;
}

const DECLINE_INTRODUCTION_RESPONSE_TEXT = "No";
const DECLINE_INTRODUCTION_ASSISTANT_TEXT =
  "No problem. You can always come back to this page when you're ready to get started.";

function getJobGetClickId(): string | undefined {
  const clickId = new URLSearchParams(window.location.search)
    .get("click_id")
    ?.trim();

  return clickId || undefined;
}

// Personal invite links append ?invite=<token> (set by the ATS invite email
// and forwarded by the 2.0 app's redirect). The API matches the session back
// to the originating ATS application by this token instead of by email.
// Pass it through as-is: the API sanitizes malformed values server-side
// (best-effort matching — a mangled link never blocks the session start).
function getInvitationToken(): string | undefined {
  const token = new URLSearchParams(window.location.search)
    .get("invite")
    ?.trim();

  return token || undefined;
}

// AS-1808: Scan assistant messages from newest to oldest to recover the most
// recent explicit values for canSkip / recordingDurationSeconds. Mirrors the
// "omit means no change" semantics from handleMessage so resume hydration does
// not drop voice metadata when the last assistant message is a non-voice
// follow-up (e.g. an ack between voice questions).
function findLatestVoiceMetadata(messages: Message[]): {
  canSkip: boolean;
  recordingDurationSeconds: number | undefined;
} {
  let canSkip: boolean | undefined;
  let recordingDurationSeconds: number | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message || message.role !== MessageRole.ASSISTANT) {
      continue;
    }
    if (canSkip === undefined && message.canSkip !== undefined) {
      canSkip = message.canSkip;
    }
    if (
      recordingDurationSeconds === undefined &&
      message.recordingDurationSeconds !== undefined
    ) {
      recordingDurationSeconds = message.recordingDurationSeconds;
    }
    if (canSkip !== undefined && recordingDurationSeconds !== undefined) {
      break;
    }
  }
  return {
    canSkip: canSkip ?? false,
    recordingDurationSeconds,
  };
}

// AS-1564: scan assistant messages newest→oldest to recover the most recent
// progress fields on resume. The screener-API attaches progress to the latest
// *assistant* row, which is NOT necessarily the last row — if the candidate
// answered (adding a user row) and then saved/refreshed before the next
// assistant response, the last resumeMessages row is a user turn. Scanning
// backward (mirrors findLatestVoiceMetadata) ensures the bar rehydrates from
// that trailing assistant row regardless of the last-message input branch.
function findLatestProgressFields(messages: Message[]): {
  currentQuestionNumber: number | undefined;
  totalQuestions: number | undefined;
} {
  // Collect each field independently (mirrors findLatestVoiceMetadata's
  // "omit means no change" semantics) so a field present only on an older
  // assistant row isn't dropped just because a newer assistant row carried
  // the other field. In practice the API emits both together, but matching
  // the hook's independent omit-vs-preserve handling keeps this robust.
  let currentQuestionNumber: number | undefined;
  let totalQuestions: number | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!message || message.role !== MessageRole.ASSISTANT) {
      continue;
    }
    if (
      currentQuestionNumber === undefined &&
      message.currentQuestionNumber !== undefined
    ) {
      currentQuestionNumber = message.currentQuestionNumber;
    }
    if (totalQuestions === undefined && message.totalQuestions !== undefined) {
      totalQuestions = message.totalQuestions;
    }
    if (currentQuestionNumber !== undefined && totalQuestions !== undefined) {
      break;
    }
  }
  return { currentQuestionNumber, totalQuestions };
}

/**
 * Locate the answer bubble being edited. NOTE: in live (non-resumed)
 * sessions optimistic user bubbles carry client-generated UUIDs, so the
 * exact-id match only hits on resumed transcripts — the positional fallback
 * below is the COMMON path, not the exception. Don't "simplify" it away.
 *
 * The exact-id match is constrained to USER messages on purpose: the
 * orchestrator's Pusher response echoes the originating user messageId as
 * the assistant turn's id (request/response correlation), so the *next
 * question* bubble shares the id of the answer we're editing. Matching by
 * id alone would select that assistant bubble and slice AFTER it, leaving
 * the stale answer in the chat (AS-1920 bug).
 */
function findPreviousEditableUserMessageIndex(
  messages: Message[],
  messageId?: string,
): number {
  if (messageId) {
    const exactIndex = messages.findIndex(
      (message) =>
        message.id === messageId && message.role === MessageRole.USER,
    );
    if (exactIndex >= 0) {
      return exactIndex;
    }
  }

  // Reverse loop per file convention (findLatestProgressFields et al).
  let lastAssistantIndex = -1;
  for (let index = messages.length - 1; index >= 0; index--) {
    if (messages[index]?.role === MessageRole.ASSISTANT) {
      lastAssistantIndex = index;
      break;
    }
  }

  const startIndex =
    lastAssistantIndex === -1 ? messages.length - 1 : lastAssistantIndex - 1;
  for (let index = startIndex; index >= 0; index--) {
    if (messages[index]?.role === MessageRole.USER) {
      return index;
    }
  }

  return -1;
}

/**
 * SRN-715 responseFormat derivation, shared by the forward send and the
 * edit submit so the two can't drift: explicit (voice) wins, else choice
 * when options were selected, else written.
 */
export function deriveResponseFormat(
  options?: SendMessageOptions,
): InterviewMessageResponseFormat {
  return (
    options?.responseFormat ??
    (options?.selectedOptionIds?.length ? "choice" : "written")
  );
}

function makeLoadingMessage(): Message {
  return {
    id: generateUUID(),
    role: MessageRole.ASSISTANT,
    content: "",
    timestamp: new Date(),
    status: RequestStates.Loading,
  };
}

function buildEditModeMessages(
  messages: Message[],
  targetClientId: string,
  presentationPrompt: string,
): Message[] | null {
  const currentIndex = messages.findIndex(
    (message) =>
      message.id === targetClientId && message.role === MessageRole.USER,
  );
  if (currentIndex < 0) {
    return null;
  }

  const nextMessages = messages.slice(0, currentIndex);
  if (!presentationPrompt.trim()) {
    return nextMessages;
  }

  for (let index = nextMessages.length - 1; index >= 0; index--) {
    if (nextMessages[index]?.role === MessageRole.ASSISTANT) {
      return nextMessages.map((message, messageIndex) =>
        messageIndex === index
          ? { ...message, content: presentationPrompt }
          : message,
      );
    }
  }

  return nextMessages;
}

interface UseInterviewReturn {
  messages: Message[];
  isConnected: boolean;
  isLoading: boolean;
  phase: InterviewPhase;
  status: InterviewUIStatus;
  sessionId: string | null;
  /** Whether it's the user's turn to respond */
  usersTurn: boolean;
  /** Current question's input type */
  questionInputType: InterviewQuestionInputType | null;
  /** Current question's ID */
  currentQuestionId: string | null;
  /** Current question's options (for choice questions) */
  questionOptions: InputOption[];
  /** Presigned upload URL + S3 key for voice questions, or null */
  voiceUrls: VoiceUrls | null;
  /** Response format for voice/written assessment questions */
  responseFormat: ResponseFormat | undefined;
  /** Whether the candidate can skip the current voice question */
  canSkip: boolean;
  /** Maximum recording duration for the current voice question */
  recordingDurationSeconds: number | undefined;
  /** Lets the candidate switch from voice mode to text mode for voice_recommended questions */
  setQuestionInputType: (inputType: InterviewQuestionInputType) => void;
  /** Seconds since last message was received */
  timeSinceLastMessage: number;
  /** Candidate-selected language for multilingual sessions */
  languageCode: string | null;
  cancelReason: "declined_intro" | null;
  /**
   * True when session creation was refused because the location is paused
   * (backend 409 with reason "location_paused"). The candidate should see a
   * "not accepting candidates" screen instead of a stuck/empty interview.
   */
  notAcceptingCandidates: boolean;
  /** AS-1564: 1-based progress position from the most recent event that carried it (omit-vs-preserve). */
  currentQuestionNumber: number | undefined;
  /** AS-1564: total counted questions for the interview. */
  totalQuestions: number | undefined;
  canEditPreviousResponse: boolean;
  isEditingPreviousResponse: boolean;
  editTarget: EditTargetState | null;
  isBackLoading: boolean;
  /**
   * AS-1564: one-shot guard for `Progress Indicator Viewed`. Returns `true`
   * the first time it's called for a given sessionId, `false` thereafter.
   * Lives on the hook so the ref survives ChatContainer / Header remounts
   * (StrictMode double-mount, route transitions, language-change remounts).
   */
  markProgressViewFired: (sessionId: string) => boolean;
  startInterview: () => Promise<void>;
  declineIntroduction: () => void;
  sendMessage: (content: string, options?: SendMessageOptions) => Promise<void>;
  requestPreviousQuestionEdit: () => Promise<EditTargetState | null>;
  submitPreviousQuestionEdit: (
    content: string,
    options?: SendMessageOptions,
  ) => Promise<void>;
  endInterview: () => Promise<void>;
}

/**
 * The slice of PreviousQuestionResponse the edit UI actually consumes
 * (voice fields are gated off until the deferred voice-editing increment).
 * resetKey drives the key-remount that seeds the prefilled inputs.
 */
export type EditTargetState = Pick<
  PreviousQuestionResponse,
  | "messageId"
  | "questionId"
  | "questionType"
  | "prompt"
  | "content"
  | "selectedOptionIds"
  | "inputType"
  | "options"
> & { resetKey: number };

/**
 * Media-answer editing is deferred behind a follow-up increment (AS-1912
 * brief open question 8 — the Figma prototype has no voice/file-editing flow).
 * Thrown by requestPreviousQuestionEdit so the caller can show a friendly
 * notice instead of the error path.
 */
export class VoiceEditUnsupportedError extends Error {
  constructor() {
    super("Editing this answer is not available yet");
    this.name = "VoiceEditUnsupportedError";
  }
}

export function useInterview({
  interviewLocationId,
  candidateId,
  onError,
  onLanguageChange,
  autoStart = true,
  resumeSessionId,
  resumeMessages,
  resumePusherChannel,
  resumePhase,
  resumeCanEditPreviousResponse,
}: UseInterviewOptions): UseInterviewReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesRef = useRef<Message[]>([]);
  const setMessagesState = useCallback((value: SetStateAction<Message[]>) => {
    setMessages((prev) => {
      const next =
        typeof value === "function"
          ? (value as (prev: Message[]) => Message[])(prev)
          : value;
      messagesRef.current = next;
      return next;
    });
  }, []);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phase, setPhase] = useState<InterviewPhase>(
    InterviewPhase.INTRODUCTION,
  );
  const [status, setStatus] = useState<InterviewUIStatus>(
    InterviewUIStatus.PENDING,
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [usersTurn, setUsersTurn] = useState(false);
  const [questionInputType, setQuestionInputType] =
    useState<InterviewQuestionInputType | null>(null);
  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(
    null,
  );
  const [questionOptions, setQuestionOptions] = useState<InputOption[]>([]);
  // SRN-715: track the latest question id + type so user replies can echo
  // them back to the API for self-describing user-turn rows.
  const currentQuestionIdRef = useRef<string | null>(null);
  const currentQuestionTypeRef = useRef<
    Required<SendMessageRequest>["questionType"] | null
  >(null);
  const currentQuestionResponseFormatRef = useRef<ResponseFormat | undefined>(
    undefined,
  );
  const [voiceUrls, setVoiceUrls] = useState<VoiceUrls | null>(null);
  const [responseFormat, setResponseFormat] = useState<
    ResponseFormat | undefined
  >(undefined);
  const [canSkip, setCanSkip] = useState(false);
  const [recordingDurationSeconds, setRecordingDurationSeconds] = useState<
    number | undefined
  >(undefined);
  const [timeSinceLastMessage, setTimeSinceLastMessage] = useState(0);
  const [languageCode, setLanguageCode] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<"declined_intro" | null>(
    null,
  );
  const [notAcceptingCandidates, setNotAcceptingCandidates] = useState(false);
  // AS-1564: progress fields. Omit-vs-preserve contract — events without
  // the fields (status-only events, the synthetic contentless completed
  // event, disconnect/reconnect blips) leave the last value intact. The
  // bar persists at the most recent emitted value until a new event
  // explicitly updates it.
  const [currentQuestionNumber, setCurrentQuestionNumber] = useState<
    number | undefined
  >(undefined);
  const [totalQuestions, setTotalQuestions] = useState<number | undefined>(
    undefined,
  );
  const [canEditPreviousResponse, setCanEditPreviousResponse] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTargetState | null>(null);
  // Derived, not stored: the two could only ever desync by bug.
  const isEditingPreviousResponse = editTarget !== null;
  const [isBackLoading, setIsBackLoading] = useState(false);

  const pusherServiceRef = useRef<PusherService | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const previousQuestionEditSubmitInFlightRef = useRef(false);
  // AS-1564: dedupe ref for the `Progress Indicator Viewed` one-shot.
  // Keyed by sessionId so resume re-entry registers as a fresh fire only
  // if the candidate runs a different session; same session across
  // ChatContainer/Header remounts is suppressed.
  const firedProgressViewSessionsRef = useRef<Set<string>>(new Set());
  const markProgressViewFired = useCallback((sessionId: string): boolean => {
    if (firedProgressViewSessionsRef.current.has(sessionId)) {
      return false;
    }
    firedProgressViewSessionsRef.current.add(sessionId);
    return true;
  }, []);

  const handleMessage = useCallback(
    (message: Message) => {
      setMessagesState((prev) => {
        // If this is an assistant message, replace any loading assistant messages
        if (message.role === MessageRole.ASSISTANT) {
          const withoutLoading = prev.filter(
            (m) =>
              !(
                m.role === MessageRole.ASSISTANT &&
                m.status === RequestStates.Loading
              ),
          );
          return [...withoutLoading, message];
        }
        return [...prev, message];
      });

      // Update question type and options from incoming assistant message
      if (message.role === MessageRole.ASSISTANT) {
        // AS-1565: an incoming assistant turn always means the server has
        // moved past any locally-stored edit context — without this, an ack
        // event landing in the gap between entering edit mode and the next
        // question event leaves the UI half-edit/half-live (stale prefill
        // keyed against a new question, PATCH aimed at an old messageId).
        // Accepted gap: bubbles pruned on edit entry are NOT restored here,
        // so the transcript can briefly miss the abandoned answer; the next
        // refresh re-fetches the server transcript and self-heals.
        setEditTarget(null);
        setQuestionInputType(
          message.inputType || InterviewQuestionInputType.TEXT,
        );
        setCurrentQuestionId(message.questionId ?? null);
        setQuestionOptions(message.options || []);
        // SRN-715: cache question id + type for echo-back on the next user reply
        currentQuestionIdRef.current = message.questionId ?? null;
        currentQuestionTypeRef.current = message.questionType ?? null;
        // voiceUrls and responseFormat are per-question and feed the
        // ChatContainer "Record instead" toggle on text input
        // (`getCanSwitchToVoice(voiceUrls, responseFormat)`). Stale values
        // from a prior voice_recommended question would surface the button on
        // a text follow-up and let the candidate jump back into the Voice
        // component with already-expired S3 upload URLs — so reset both
        // whenever the new question is not a voice question.
        const isVoiceQuestion =
          message.inputType === InterviewQuestionInputType.VOICE;
        if (message.uploadUrl && message.audioUrl) {
          setVoiceUrls({
            uploadUrl: message.uploadUrl,
            audioUrl: message.audioUrl,
          });
        } else if (!isVoiceQuestion) {
          setVoiceUrls(null);
        }
        if (message.responseFormat !== undefined) {
          setResponseFormat(message.responseFormat);
          currentQuestionResponseFormatRef.current = message.responseFormat;
        } else if (!isVoiceQuestion) {
          setResponseFormat(undefined);
          currentQuestionResponseFormatRef.current = undefined;
        }
        // recordingDurationSeconds and canSkip survive non-voice turns
        // (AS-1808): omitting these from a Pusher message means "no change",
        // not "reset to default". Without this, ack/status messages between
        // voice questions cleared the duration and the Voice component fell
        // back to its 60s/1:00 default — the AS-1783 regression candidates
        // were seeing in interviewer_frontend.
        if (message.canSkip !== undefined) {
          setCanSkip(message.canSkip);
        }
        if (message.recordingDurationSeconds !== undefined) {
          setRecordingDurationSeconds(message.recordingDurationSeconds);
        }
        // AS-1564: omit-vs-preserve contract for progress fields. Events
        // without these fields (status-only, the synthetic completed event,
        // disconnect blips) leave the last value untouched. Without this,
        // the bar would disappear the moment any event arrives without
        // progress data — defeating the resilience point of having the
        // backend be source-of-truth.
        if (message.currentQuestionNumber !== undefined) {
          setCurrentQuestionNumber(message.currentQuestionNumber);
        }
        if (message.totalQuestions !== undefined) {
          setTotalQuestions(message.totalQuestions);
        }
        setUsersTurn(true);
        setTimeSinceLastMessage(0);
        if (message.language) {
          setLanguageCode(message.language);
          onLanguageChange?.(message.language);
        }
        // Clear timer on message received
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    },
    [onLanguageChange, setMessagesState],
  );

  const handleStatus = useCallback(
    (statusUpdate: { phase?: string; status?: string }) => {
      if (statusUpdate.phase) {
        setPhase(statusUpdate.phase as InterviewPhase);
      }
      // Map backend session status to UI status
      // Backend sends: 'new', 'partial', 'complete', 'error', 'empty'
      // UI expects: 'pending', 'in_progress', 'completed', 'cancelled'
      if (statusUpdate.status) {
        const backendStatus = statusUpdate.status.toLowerCase();
        if (backendStatus === "complete" || backendStatus === "completed") {
          setStatus(InterviewUIStatus.COMPLETED);
          setCanEditPreviousResponse(false);
          // AS-1565: an edited qualifying answer can flip pass→fail, so the
          // completed event can land right after entering edit mode with no
          // new question — exit edit mode so the completion screen renders.
          setEditTarget(null);
        } else if (backendStatus === "error" || backendStatus === "cancelled") {
          setStatus(InterviewUIStatus.CANCELLED);
          setCanEditPreviousResponse(false);
          setCancelReason(null);
          setEditTarget(null);
        }
        // For 'new' or other statuses, keep the current UI status (IN_PROGRESS)
      }
    },
    [],
  );

  const handleError = useCallback(
    (error: string) => {
      console.error("Interview error:", error);
      onError?.(error);
    },
    [onError],
  );

  const startInterview = useCallback(async () => {
    setIsLoading(true);
    setCancelReason(null);
    setNotAcceptingCandidates(false);
    try {
      // Generate session ID upfront so we can subscribe to Pusher first
      const tempSessionId = generateUUID();
      const pusherChannel = `interview-${tempSessionId}`;

      // Connect to Pusher FIRST and wait for subscription
      pusherServiceRef.current = new PusherService(
        config.pusherKey,
        config.pusherCluster,
        pusherChannel,
        handleMessage,
        handleStatus,
        handleError,
      );

      // Wait for Pusher subscription to succeed before calling API
      await pusherServiceRef.current.connect();
      setIsConnected(true);

      // Now call API with the pre-generated session ID
      const response = await apiService.startInterview({
        interviewLocationId,
        candidateId,
        sessionId: tempSessionId,
        jobgetClickId: getJobGetClickId(),
        invitationToken: getInvitationToken(),
      });

      setSessionId(response.sessionId);
      setCanEditPreviousResponse(response.canEditPreviousResponse ?? false);
      setStatus(InterviewUIStatus.IN_PROGRESS);
    } catch (error) {
      // A paused location returns 409 with reason "location_paused" (see the
      // backend screener-location-access helper). Show the candidate the
      // "not accepting candidates" screen instead of a generic error.
      if (isLocationPausedError(error)) {
        setNotAcceptingCandidates(true);
      } else {
        handleError(
          error instanceof Error ? error.message : "Failed to start interview",
        );
      }
      // Cleanup Pusher on error
      if (pusherServiceRef.current) {
        pusherServiceRef.current.disconnect();
        pusherServiceRef.current = null;
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    interviewLocationId,
    candidateId,
    handleMessage,
    handleStatus,
    handleError,
  ]);

  const declineIntroduction = useCallback(() => {
    setMessagesState((prev) => [
      ...prev,
      {
        id: generateUUID(),
        role: MessageRole.USER,
        content: DECLINE_INTRODUCTION_RESPONSE_TEXT,
        metadata: { translateStaticContent: true },
        timestamp: new Date(),
        status: RequestStates.Success,
      },
      {
        id: generateUUID(),
        role: MessageRole.ASSISTANT,
        content: DECLINE_INTRODUCTION_ASSISTANT_TEXT,
        metadata: { translateStaticContent: true },
        timestamp: new Date(),
        status: RequestStates.Success,
      },
    ]);
    setUsersTurn(false);
    setQuestionInputType(null);
    setCurrentQuestionId(null);
    setQuestionOptions([]);
    setCancelReason("declined_intro");
    setStatus(InterviewUIStatus.CANCELLED);
    setIsLoading(false);
    setCanEditPreviousResponse(false);
    setEditTarget(null);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (pusherServiceRef.current) {
      pusherServiceRef.current.disconnect();
      pusherServiceRef.current = null;
    }
    setIsConnected(false);
  }, [setMessagesState]);

  const sendMessage = useCallback(
    async (content: string, options?: SendMessageOptions) => {
      if (!sessionId) {
        handleError("No active session");
        return;
      }

      // Set usersTurn to false since we're waiting for response
      setUsersTurn(false);
      setTimeSinceLastMessage(0);

      // Voice responses transport an S3 key (s3://bucket/path) as the
      // message content. The raw key is stored in the optimistic message
      // and normalized to a friendly bubble at the MessageContent render
      // layer so live and resumed messages render identically.
      const answerResponseFormat = deriveResponseFormat(options);
      const userMessage: Message = {
        id: generateUUID(),
        role: MessageRole.USER,
        content: options?.displayContent ?? content,
        timestamp: new Date(),
        status: RequestStates.Success,
        inputType: options?.inputType,
        questionId: currentQuestionIdRef.current ?? undefined,
        questionType: currentQuestionTypeRef.current ?? undefined,
        responseFormat: currentQuestionResponseFormatRef.current,
        answerResponseFormat,
        ...(options?.audioS3Key && { audioS3Key: options.audioS3Key }),
      };
      setMessagesState((prev) => [...prev, userMessage]);

      // Add loading message placeholder for the assistant
      const loadingMessage = makeLoadingMessage();
      setMessagesState((prev) => [...prev, loadingMessage]);

      setIsLoading(true);

      // Start timer to track time since message sent
      timerRef.current = setInterval(() => {
        setTimeSinceLastMessage((prev) => prev + 1);
      }, 1000);

      try {
        // SRN-715: echo question metadata back so user-turn rows in
        // interview_session_message carry question_type + question id.
        // Voice answers pass `options.responseFormat = 'voice'` explicitly
        // (along with `audioS3Key` for the recording's s3:// key). Other
        // turns derive responseFormat from the input shape — choice when
        // options were selected, written otherwise.
        //
        // Precedence assumption: explicit voice + selectedOptionIds are
        // mutually exclusive in current UI (voice questions don't render
        // a choice picker). If a future mixed-mode question violates that,
        // the explicit responseFormat below would drop the choice signal —
        // intercept here before that ever ships.
        await apiService.sendMessage({
          sessionId,
          content,
          selectedOptionIds: options?.selectedOptionIds,
          ...(currentQuestionIdRef.current && {
            interviewQuestionId: currentQuestionIdRef.current,
          }),
          ...(currentQuestionTypeRef.current && {
            questionType: currentQuestionTypeRef.current,
          }),
          responseFormat: answerResponseFormat,
          ...(options?.audioS3Key && { audioS3Key: options.audioS3Key }),
        });
        // Note: Response will come via Pusher, which will replace the loading message
      } catch (error) {
        // Remove loading message on error
        setMessagesState((prev) =>
          prev.filter((m) => m.id !== loadingMessage.id),
        );
        setUsersTurn(true);
        // Clear timer on error
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        handleError(
          error instanceof Error ? error.message : "Failed to send message",
        );
        // Rethrow so callers (e.g. Voice playback) can surface the failure to
        // the user and recover instead of falsely treating send as success.
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, handleError, setMessagesState],
  );

  const requestPreviousQuestionEdit =
    useCallback(async (): Promise<EditTargetState | null> => {
      if (!sessionId) {
        handleError("No active session");
        return null;
      }

      setIsBackLoading(true);
      try {
        const response = await apiService.getPreviousQuestion(sessionId);

        // The server has the final say on editability — it can decline for
        // reasons the client can't see (state changed since render, server-
        // side races). Entering edit mode anyway would strand the candidate
        // on a PATCH that can only fail.
        if (!response.canEditPreviousResponse) {
          throw new Error("Editing the previous response is not available");
        }

        // Media-answer editing is deferred (brief open question 8: the Figma
        // prototype has no voice/file-editing flow). Abandon before any UI state
        // changes — the server-stored edit context is invalidated by the next
        // forward message or its own TTL.
        if (
          response.audioS3Key ||
          response.answerResponseFormat === "voice" ||
          response.inputType === InterviewQuestionInputType.VOICE ||
          response.inputType === InterviewQuestionInputType.FILE_UPLOAD
        ) {
          throw new VoiceEditUnsupportedError();
        }
        const currentMessages = messagesRef.current;
        const targetMessageIndex = findPreviousEditableUserMessageIndex(
          currentMessages,
          response.messageId,
        );
        if (targetMessageIndex < 0) {
          throw new Error("Previous answer is not available in the transcript");
        }
        // Pin the prune to the bubble's CLIENT-side id (optimistic bubbles
        // carry client UUIDs): the updater below then can't resolve a
        // different row than the one validated above, so edit mode can never
        // be entered without the matching prune having applied.
        const targetClientId = currentMessages[targetMessageIndex]?.id;
        if (!targetClientId) {
          throw new Error("Previous answer is not available in the transcript");
        }
        const preserveExistingPresentation =
          response.presentationLocalized === false;
        const existingPresentationMessage = preserveExistingPresentation
          ? (() => {
              for (let index = targetMessageIndex - 1; index >= 0; index--) {
                const message = currentMessages[index];
                if (message?.role === MessageRole.ASSISTANT) {
                  return message;
                }
              }
              return undefined;
            })()
          : undefined;
        const presentationPrompt = preserveExistingPresentation
          ? (existingPresentationMessage?.content ?? response.prompt)
          : response.prompt;
        const presentationOptions = preserveExistingPresentation
          ? (existingPresentationMessage?.options ?? response.options)
          : response.options;

        const nextEditTarget: EditTargetState = {
          messageId: response.messageId,
          questionId: response.questionId,
          questionType: response.questionType,
          prompt: presentationPrompt,
          content: response.content,
          selectedOptionIds: response.selectedOptionIds,
          inputType: response.inputType,
          options: presentationOptions,
          resetKey: Date.now(),
        };

        // Validate against the ref snapshot so we can throw before entering
        // edit mode. The actual mutation below uses a functional updater so a
        // concurrent handleMessage can't be clobbered.
        if (
          !buildEditModeMessages(
            currentMessages,
            targetClientId,
            presentationPrompt,
          )
        ) {
          throw new Error("Previous answer is not available in the transcript");
        }

        // Per the Figma spec: the candidate's answer bubble is REMOVED from
        // the chat (its content prefills the input instead), so the question
        // being edited becomes the last visible assistant bubble.
        // Use a functional updater so the prune is applied to the latest
        // messages, not the ref snapshot read above — a Pusher-driven
        // handleMessage that lands in between would otherwise be clobbered.
        setMessagesState(
          (prev) =>
            buildEditModeMessages(prev, targetClientId, presentationPrompt) ??
            prev,
        );

        setEditTarget(nextEditTarget);
        setUsersTurn(true);
        setQuestionInputType(
          response.inputType ?? InterviewQuestionInputType.TEXT,
        );
        setCurrentQuestionId(response.questionId ?? null);
        setQuestionOptions(presentationOptions ?? []);
        currentQuestionIdRef.current = response.questionId ?? null;
        currentQuestionTypeRef.current = response.questionType ?? null;
        // Text/choice editing only — never offer the voice affordances for
        // the replacement answer, even on voice-enabled questions.
        setVoiceUrls(null);
        setResponseFormat(undefined);
        currentQuestionResponseFormatRef.current = undefined;
        setCanSkip(false);
        setTimeSinceLastMessage(0);
        previousQuestionEditSubmitInFlightRef.current = false;
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        // Returned so callers (analytics) can attribute the event to the
        // question actually entered, not whatever closure state they hold.
        return nextEditTarget;
      } catch (error) {
        if (!(error instanceof VoiceEditUnsupportedError)) {
          handleError(
            error instanceof Error
              ? error.message
              : "Failed to reopen previous question",
          );
        }
        throw error;
      } finally {
        setIsBackLoading(false);
      }
    }, [sessionId, handleError, setMessagesState]);

  const submitPreviousQuestionEdit = useCallback(
    async (content: string, options?: SendMessageOptions) => {
      if (!sessionId || !editTarget) {
        // Throw (not just toast) so callers can't treat a dropped submit as
        // success — e.g. analytics firing PreviousQuestionEditSubmitted for
        // an edit that never left the client.
        handleError("No active edit target");
        previousQuestionEditSubmitInFlightRef.current = false;
        throw new Error("No active edit target");
      }
      if (previousQuestionEditSubmitInFlightRef.current) {
        throw new Error("Previous question edit submit is already in progress");
      }
      previousQuestionEditSubmitInFlightRef.current = true;

      setUsersTurn(false);
      setTimeSinceLastMessage(0);

      const loadingMessage = makeLoadingMessage();

      // Entry removed the old answer bubble (Figma); append the corrected
      // bubble in its place, then the loading indicator for the replayed
      // turn. The filter guards against a duplicate if the old bubble
      // somehow survived entry (e.g. resumed mid-edit).
      const editedMessage: Message = {
        id: editTarget.messageId,
        role: MessageRole.USER,
        content: options?.displayContent ?? content,
        timestamp: new Date(),
        status: RequestStates.Success,
        inputType: options?.inputType,
        questionId: editTarget.questionId ?? undefined,
        questionType: editTarget.questionType ?? undefined,
      };
      setMessagesState((prev) => [
        // Remove only the USER bubble for this id if it survived entry — an
        // assistant turn can share this id (orchestrator echoes the user
        // messageId on its Pusher response), and we must not drop a question.
        ...prev.filter(
          (message) =>
            !(
              message.id === editTarget.messageId &&
              message.role === MessageRole.USER
            ),
        ),
        editedMessage,
        loadingMessage,
      ]);

      setIsLoading(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        setTimeSinceLastMessage((prev) => prev + 1);
      }, 1000);

      try {
        const responseFormat = deriveResponseFormat(options);

        await apiService.editPreviousResponse({
          sessionId,
          messageId: editTarget.messageId,
          content,
          selectedOptionIds: options?.selectedOptionIds,
          ...(editTarget.questionId && {
            interviewQuestionId: editTarget.questionId,
          }),
          ...(editTarget.questionType && {
            questionType: editTarget.questionType,
          }),
          responseFormat,
          ...(options?.audioS3Key && { audioS3Key: options.audioS3Key }),
        });

        setEditTarget(null);
      } catch (error) {
        setMessagesState((prev) =>
          prev.filter((m) => m.id !== loadingMessage.id),
        );
        setUsersTurn(true);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        handleError(
          error instanceof Error
            ? error.message
            : "Failed to submit edited answer",
        );
        throw error;
      } finally {
        setIsLoading(false);
        previousQuestionEditSubmitInFlightRef.current = false;
      }
    },
    [sessionId, editTarget, handleError, setMessagesState],
  );

  const endInterview = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    setIsLoading(true);
    try {
      await apiService.endInterview(sessionId);
      setStatus(InterviewUIStatus.COMPLETED);
      setCanEditPreviousResponse(false);
    } catch (error) {
      handleError(
        error instanceof Error ? error.message : "Failed to end interview",
      );
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, handleError]);

  // Resume mode: initialize with existing session data and connect to Pusher
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (!resumeSessionId || !resumeMessages || hasResumedRef.current) {
      return;
    }
    hasResumedRef.current = true;

    setSessionId(resumeSessionId);
    setMessagesState(resumeMessages);
    setPhase(resumePhase ?? InterviewPhase.QUESTIONS);
    setStatus(InterviewUIStatus.IN_PROGRESS);
    setCanEditPreviousResponse(resumeCanEditPreviousResponse ?? false);

    // Set input state based on last message
    const lastMessage = resumeMessages[resumeMessages.length - 1];
    const resumeHasPendingAssistantTurn =
      lastMessage?.role === MessageRole.ASSISTANT;

    if (lastMessage?.role === MessageRole.ASSISTANT) {
      setQuestionInputType(
        lastMessage.inputType ?? InterviewQuestionInputType.TEXT,
      );
      setCurrentQuestionId(lastMessage.questionId ?? null);
      setQuestionOptions(lastMessage.options ?? []);
      if (lastMessage.uploadUrl && lastMessage.audioUrl) {
        setVoiceUrls({
          uploadUrl: lastMessage.uploadUrl,
          audioUrl: lastMessage.audioUrl,
        });
      } else {
        setVoiceUrls(null);
      }
      setResponseFormat(lastMessage.responseFormat);
      currentQuestionResponseFormatRef.current = lastMessage.responseFormat;
      const {
        canSkip: rehydratedCanSkip,
        recordingDurationSeconds: rehydratedRecordingDurationSeconds,
      } = findLatestVoiceMetadata(resumeMessages);
      setCanSkip(rehydratedCanSkip);
      setRecordingDurationSeconds(rehydratedRecordingDurationSeconds);
    } else {
      setQuestionInputType(InterviewQuestionInputType.TEXT);
      setCurrentQuestionId(null);
      setQuestionOptions([]);
      setVoiceUrls(null);
      setResponseFormat(undefined);
      currentQuestionResponseFormatRef.current = undefined;
      setCanSkip(false);
      setRecordingDurationSeconds(undefined);
    }

    // AS-1564: rehydrate progress fields independent of the last-message
    // input-state branch above. The API attaches progress to the latest
    // assistant row, which may be followed by a user row (candidate answered
    // then saved/refreshed before the next assistant reply). Scanning backward
    // recovers it either way so the bar isn't blank until the next Pusher
    // event. (Justen review finding on #3529.)
    const {
      currentQuestionNumber: rehydratedCurrentQuestionNumber,
      totalQuestions: rehydratedTotalQuestions,
    } = findLatestProgressFields(resumeMessages);
    if (rehydratedCurrentQuestionNumber !== undefined) {
      setCurrentQuestionNumber(rehydratedCurrentQuestionNumber);
    }
    if (rehydratedTotalQuestions !== undefined) {
      setTotalQuestions(rehydratedTotalQuestions);
    }

    // Resume transcripts are rendered before the WebSocket reconnect completes.
    // Keep input disabled until Pusher subscribes so candidates cannot answer
    // and miss the final response/completed event during the reconnect race.
    setUsersTurn(false);

    // Connect to Pusher for real-time responses
    const pusherChannel = resumePusherChannel ?? `interview-${resumeSessionId}`;

    pusherServiceRef.current = new PusherService(
      config.pusherKey,
      config.pusherCluster,
      pusherChannel,
      handleMessage,
      handleStatus,
      handleError,
    );

    pusherServiceRef.current
      .connect()
      .then(() => {
        setIsConnected(true);
        setUsersTurn((prev) => prev || resumeHasPendingAssistantTurn);
      })
      .catch((err) => {
        handleError(
          err instanceof Error
            ? err.message
            : "Failed to connect to Pusher for resume",
        );
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-start interview if enabled - runs once on mount (skip in resume mode)
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (resumeSessionId) {
      return;
    }
    if (autoStart && !hasStartedRef.current) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        // Check again inside timeout to handle StrictMode double-invocation
        if (!hasStartedRef.current) {
          hasStartedRef.current = true;
          startInterview();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount (reset refs so StrictMode remount can re-initialize)
  useEffect(() => {
    return () => {
      if (pusherServiceRef.current) {
        pusherServiceRef.current.disconnect();
        pusherServiceRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      hasResumedRef.current = false;
    };
  }, []);

  return {
    messages,
    isConnected,
    isLoading,
    phase,
    status,
    sessionId,
    usersTurn,
    questionInputType,
    currentQuestionId,
    questionOptions,
    voiceUrls,
    responseFormat,
    canSkip,
    recordingDurationSeconds,
    setQuestionInputType,
    timeSinceLastMessage,
    languageCode,
    cancelReason,
    notAcceptingCandidates,
    currentQuestionNumber,
    totalQuestions,
    canEditPreviousResponse,
    isEditingPreviousResponse,
    editTarget,
    isBackLoading,
    markProgressViewFired,
    startInterview,
    declineIntroduction,
    sendMessage,
    requestPreviousQuestionEdit,
    submitPreviousQuestionEdit,
    endInterview,
  };
}
